import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Menu,
  MenuItem,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  ArrowDropUp,
  ArrowDropDown,
  KeyboardArrowDown,
  PlayArrow,
  UnfoldMore,
  BorderColor,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import OrderEntry from "./OrderEntry";
import TradeEntryPopup from "./TradeEntry/TradeEntryPopup";
import { applyRenderFunctions } from "./OrderComponents/columnRendererRegistry";
import { updateColumnConfigIfNeeded } from "../Utils/columnVersionManager";
import { getPreference, setPreference } from "../Utils/userPreferenceManger";
import { NUMERIC_COLUMNS } from '../Constants/globalColConfig';
import FilterChips from "./InventoryComponents/FilterChips";
import { parseCharacteristics, preprocessFilters } from "./OrderComponents/columnConfigUtils";
import {
  formatNumber,
  isNumericOrDate,
  isBuySellColumn,
  isTypeLikeColumn,
  isBoldColumn,
  isGreenColumn,
  getLadderChips,
  getProcessedLadderEditData,
  isLadderLinkedCharacteristicGroup,
  prefillSelectedLadderOrders, // <-- import the new util
} from "./StagingBlotter/StagingBlotterUtils";
import { handleViewInventory } from "../Utils/viewInventoryHandler";
import { processBondData } from "./OrderComponents/utils";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthProvider"; // <-- add this import
import { useBondLadderRungFlowStore } from "../Store/bondLadderRungFlowStore";
import { tokens, withAlpha } from "../Common/designTokens";

// Map color constants to tokens (light theme)
const MAIN_BG_COLOR = tokens.colors.defaultGridRowColor;
const MAIN_BG_COLOR_DARK = tokens.colors.defaultGridHoverColor || '#e9ecef'; // Use a more contrasting gray
const LADDER_CHILD_BG_COLOR = tokens.colors.defaultGridHoverColor; // Even lighter gray for child rows
const HEADER_BG_COLOR = tokens.colors.defaultGridHeaderBackground;
const HOVER_BG_COLOR = tokens.colors.defaultGridHoverColor;
const LADDER_LEVEL_SEND_TO_MARKET_SOURCE = "ladder-level-send-to-market";

const StagingBlotterTable = ({
  source = null,
  selectedColumns,
  filteredData,
  handleRowClick,
  handleViewJson,
  handleBuyClick,
  handleCusipClick,
  onOrderAdded,
  orderData,
  setOrderData,
  sectionKey = "staging",
  onSort,
  sortConfig: externalSortConfig,
  isEditAllowed = true, // <-- add prop with default false
}) => {
  const tableContainerRef = useRef(null);
  const [rawColumns, setRawColumns] = useState([]);
  
  const columns = useMemo(() => {
    console.log(`StagingBlotterTable: Applying render functions to columns: source=${source}, sectionKey=${sectionKey}`);
    const cleanedSectionKey = sectionKey && sectionKey.includes('=') 
      ? sectionKey.split('=').pop() 
      : sectionKey;
    
    return applyRenderFunctions(rawColumns, source, cleanedSectionKey || sectionKey);
  }, [rawColumns, source, sectionKey]);
  
  const sortConfig = externalSortConfig || { key: null, direction: "asc" };
  const [columnWidths, setColumnWidths] = useState({});
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [orderEntryOpen, setOrderEntryOpen] = useState(false);
  const [manualTradeOpen, setManualTradeOpen] = useState(false);
  const [manualTradePrefill, setManualTradePrefill] = useState(null);
  const [editLoading, setEditLoading] = useState(false); // <-- loader state
  const userRole = localStorage.getItem("role");
  const [expandedGroups, setExpandedGroups] = useState({});

  // Animation state for expanded groups
  const [expandedAnimation, setExpandedAnimation] = useState({});

  // Group data by new ladder structure from backend
  const groupedData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    const groups = [];
    filteredData.forEach((order) => {
      if (order.bondLadderId && Array.isArray(order.ladderOrders) && order.ladderOrders.length > 0) {
        // Ladder group: use parent order for ladder info, ladderOrders for children
        groups.push({
          isLadderGroup: true,
          bondLadderId: order.bondLadderId,
          ladderName: order.nameOfTheLadder || `Ladder ${order.bondLadderId}`,
          orders: order.ladderOrders,
          ...order,
        });
      } else {
        // Individual order (not part of a ladder group)
        groups.push(order);
      }
    });

    return groups;
  }, [filteredData]);

  // Load saved column widths on initial render
  useEffect(() => {
    try {
      const savedWidths = getPreference(`columnWidths_${sectionKey}`);
      if (savedWidths) {
        const parsedWidths = savedWidths;
        if ("viewInventory" in parsedWidths) {
          parsedWidths.viewInventory = Math.max(parsedWidths.viewInventory, 180);
        }
        parsedWidths.protocol = 100;
        setColumnWidths(parsedWidths);
      }
    } catch (e) {
      console.error("Error loading saved column widths:", e);
    }
  }, [sectionKey]);

  // Save column widths whenever they change
  useEffect(() => {
    if (Object.keys(columnWidths).length > 0) {
      try {
        setPreference(`columnWidths_${sectionKey}`, columnWidths);
      } catch (e) {
        console.error("Error saving column widths:", e);
      }
    }
  }, [columnWidths, sectionKey]);

  // Calculate column widths
  const calculateMaxContentWidth = (columnKey) => {
    if (columnKey === "protocol") return 100;
    if (columnKey === "viewInventory") return 180;
    if (columnKey === "bondLadderRungId") return 80; // Fixed width for rung ID

    const savedWidth = columnWidths[columnKey];
    if (savedWidth) return savedWidth;

    const span = document.createElement("span");
    span.style.visibility = "hidden";
    span.style.position = "absolute";
    span.style.whiteSpace = "nowrap";
    document.body.appendChild(span);

    span.textContent = selectedColumns.find((col) => col.key === columnKey)?.name || "";
    let maxWidth = span.offsetWidth;

    groupedData.forEach((row) => {
      if (row.isLadderGroup) {
        // Check ladder name length
        span.textContent = row.ladderName || "";
        maxWidth = Math.max(maxWidth, span.offsetWidth);
        
        // Check orders in the group
        row.orders.forEach((order) => {
          const value = formatNumberLocal(order[columnKey], columnKey, order);
          span.textContent = value?.toString() || "";
          maxWidth = Math.max(maxWidth, span.offsetWidth);
        });
      } else {
        const value = formatNumberLocal(row[columnKey], columnKey, row);
        span.textContent = value?.toString() || "";
        maxWidth = Math.max(maxWidth, span.offsetWidth);
      }
    });

    document.body.removeChild(span);
    const padding = 40;
    const paddedWidth = maxWidth + padding;
    return Math.min(paddedWidth, 150);
  };

  // Local helper to format dates as MM/DD/YYYY for maturity fields
  const formatDateToMMDDYYYY = (dateStr) => {
    try {
      if (!dateStr) return "---";
      let valueStr = dateStr.toString();
      if (!valueStr.endsWith('Z')) valueStr += 'Z';
      const date = new Date(valueStr);
      if (isNaN(date.getTime())) return "---";
      const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(date.getUTCDate()).padStart(2, '0');
      const yyyy = date.getUTCFullYear();
      return `${mm}/${dd}/${yyyy}`;
    } catch (e) {
      console.error('Error formatting date to MM/DD/YYYY:', e, 'for value:', dateStr);
      return '---';
    }
  };
  
  // Wrapper around imported formatNumber to override maturity formatting
  const formatNumberLocal = (value, columnKey, rowData) => {
    if (!value && value !== 0) return formatNumber(value, columnKey, rowData);
    if (columnKey === 'maturity' || columnKey === 'maturityDate') {
      return formatDateToMMDDYYYY(value);
    }
    return formatNumber(value, columnKey, rowData);
  };

  // Initialize columns
  useEffect(() => {
    if (sectionKey) {
      const savedOrder = getPreference(`columnOrder_${sectionKey}`);
      if (savedOrder) {
        try {
          const parsedOrder = savedOrder;
          if (parsedOrder.length === selectedColumns.length) {
            const selectedKeysSet = new Set(selectedColumns.map(col => col.key));
            const parsedKeysSet = new Set(parsedOrder.map(col => col.key));
            const hasSameColumns = [...selectedKeysSet].every(key => parsedKeysSet.has(key));
            
            if (hasSameColumns) {
              setRawColumns(parsedOrder);
              return;
            }
          }
        } catch (e) {
          console.error("Error parsing saved column order:", e);
        }
      }
    }
    setRawColumns(selectedColumns);
    
    // Initialize column widths
    setColumnWidths((prev) => {
      const newWidths = { ...prev };
      selectedColumns.forEach((col) => {
        if (col.key === "protocol") {
          newWidths[col.key] = 100;
        } else if (!(col.key in newWidths)) {
          newWidths[col.key] = calculateMaxContentWidth(col.key);
        }
      });
      Object.keys(newWidths).forEach((key) => {
        if (!selectedColumns.some((col) => col.key === key)) {
          delete newWidths[key];
        }
      });
      newWidths.protocol = 100;
      return newWidths;
    });
  }, [selectedColumns, groupedData]);

  // Reset scroll when data changes
  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [groupedData]);

  // Sorting Logic
  const handleSort = (columnKey) => {
    const newDirection =
      sortConfig?.key === columnKey && sortConfig?.direction === "asc"
        ? "desc"
        : "asc";
    onSort(columnKey, newDirection);
  };

  // Drag and Drop Logic
  const handleDragEnd = (result) => {
    if (!result.destination || result.source.index === 0 || result.destination.index === 0) {
      return;
    }

    // Check if we're trying to drag a non-draggable column
    const draggedColumn = rawColumns[result.source.index];
    if (draggedColumn?.nonDraggable) {
      return;
    }

    // Check if we're trying to drop on a non-draggable column
    const targetColumn = rawColumns[result.destination.index];
    if (targetColumn?.nonDraggable) {
      return;
    }

    const reorderedColumns = [...rawColumns];
    const [movedColumn] = reorderedColumns.splice(result.source.index, 1);
    reorderedColumns.splice(result.destination.index, 0, movedColumn);

    setRawColumns(reorderedColumns);

    if (sectionKey) {
      setPreference(`columnOrder_${sectionKey}`, reorderedColumns);
      // ...existing code for saving full order...
    }
  };

  // Group expansion toggle with animation
  const handleGroupExpand = (event, bondLadderId) => {
    event.stopPropagation();
    setExpandedGroups((prev) => {
      const isExpanding = !prev[bondLadderId];
      if (isExpanding) {
        setExpandedAnimation((animPrev) => ({
          ...animPrev,
          [bondLadderId]: { maxHeight: 1000, opacity: 1 }
        }));
      } else {
        setExpandedAnimation((animPrev) => ({
          ...animPrev,
          [bondLadderId]: { maxHeight: 0, opacity: 0 }
        }));
        // Remove animation state after transition
        setTimeout(() => {
          setExpandedAnimation((animPrev) => {
            const copy = { ...animPrev };
            delete copy[bondLadderId];
            return copy;
          });
        }, 300);
      }
      return {
        ...prev,
        [bondLadderId]: isExpanding,
      };
    });
  };

  // Resizing logic
  const resizingColumnRef = useRef(null);
  const initialWidthRef = useRef(0);
  const initialXRef = useRef(0);

  const startResizing = (e, columnKey) => {
    e.stopPropagation();
    if (columnKey === "protocol" || columnKey === "bondLadderRungId") return;
    
    // Check if column is non-resizable
    const column = rawColumns.find(col => col.key === columnKey);
    if (column?.nonResizable) return;
    
    resizingColumnRef.current = columnKey;
    initialWidthRef.current = columnWidths[columnKey] || 150;
    initialXRef.current = e.clientX;
    document.addEventListener("mousemove", handleResizing);
    document.addEventListener("mouseup", stopResizing);
    document.body.style.cursor = "col-resize";
  };

  const handleResizing = (e) => {
    if (resizingColumnRef.current === "viewInventory" || resizingColumnRef.current === "protocol" || resizingColumnRef.current === "bondLadderRungId") {
      return;
    }
    
    // Check if column is non-resizable
    const column = rawColumns.find(col => col.key === resizingColumnRef.current);
    if (column?.nonResizable) {
      return;
    }
    
    if (resizingColumnRef.current) {
      const delta = e.clientX - initialXRef.current;
      const newWidth = initialWidthRef.current + delta;
      let minWidth = 50;
      if (resizingColumnRef.current === "viewInventory") {
        minWidth = 180;
      }
      const maxWidth = resizingColumnRef.current === "protocol" ? 100 : 500;
      const clampedWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));

      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumnRef.current]: clampedWidth,
      }));
    }
  };

  const stopResizing = () => {
    resizingColumnRef.current = null;
    document.removeEventListener("mousemove", handleResizing);
    document.removeEventListener("mouseup", stopResizing);
    document.body.style.cursor = "";
  };

  // Context Menu Handlers
  const handleContextMenu = (event, rowData) => {
    event.preventDefault();
    if (!isStagingSection) return; // only in staging blotter
    setSelectedRowData(rowData);
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null
    );
  };

  const handleCloseContextMenu = () => setContextMenu(null);

  const handleSendToMarket = () => {
    handleCloseContextMenu();
    setOrderEntryOpen(true);
  };

  const handleManualTradeCapture = () => {
    if (!selectedRowData) return;
    const prefill = {
      cusip: selectedRowData.cusip,
      side: selectedRowData.side || selectedRowData.action,
      tradeQuantity: selectedRowData.quantity ?? selectedRowData.qty,
      tradePrice: selectedRowData.price,
      pmId: selectedRowData.pmId,
      pm: selectedRowData.pm,
      account: selectedRowData.account,
      orderId: selectedRowData.orderId ?? null,
      bondInventoryId: selectedRowData.bondInventoryId ?? null,
    };
    setManualTradePrefill(prefill);
    setManualTradeOpen(true);
    handleCloseContextMenu();
  };

  // Ladder characteristics chips modal state
  const [ladderCharModal, setLadderCharModal] = useState({ open: false, chips: [], ladderName: "" });

  const rowHeight = "40px";
  const cellPadding = "4px 8px";

  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth?.token || localStorage.getItem("token");
  const currentUserId = auth?.userId || localStorage.getItem("userId");

  // helper: check if current user can act on given row (placed the order)
  const canActOn = (row) => {
    if (!row) return false;
    const entryBy = row.entryBy ?? row.orders?.[0]?.entryBy ?? row.ladderOrders?.[0]?.entryBy;
    return String(entryBy ?? "") === String(currentUserId ?? "");
  };

  // Determine modal source based on role: allow Traders and PMs to open sendToMarket
  const orderEntrySource = "sendToMarket";
  const isLadderLevelSendToMarket = Boolean(selectedRowData?.isLadderGroup);
  const orderEntrySecondarySource = isLadderLevelSendToMarket
    ? LADDER_LEVEL_SEND_TO_MARKET_SOURCE
    : undefined;

  // Handler for Edit icon click on ladder group
  const handleEditLadderGroup = async (rowData) => {
    setEditLoading(true); // show loader
    try {
      const processedData = getProcessedLadderEditData(rowData);

      // Prefill selected orders before navigating
      await prefillSelectedLadderOrders(rowData.bondLadderId, token);

      // Set processedData to ladderData in rung flow store so it's available in view inventory
      useBondLadderRungFlowStore.getState().setLadderData(processedData);

      // Call view inventory handler for ladder (activeTab=2)
      handleViewInventory(
        2,
        null,
        () => {}, // setCurrentCharacteristicID (not needed here)
        () => {}, // setActualOrderData (not needed here)
        navigate,
        (error) => {
          alert(error);
        },
        () => {}, // setLoading (not needed here)
        false // <-- do not clear all orders
      );
    } finally {
      setEditLoading(false); // hide loader
    }
  };

  // Determine if this component instance is the staging blotter main tab (order_0-0)
  const isStagingSection = sectionKey && sectionKey.includes("order_0-0");

  // Helper to trigger the "Send to market" flow (used for left-click for Trader)
  const triggerSendToMarket = (rowData, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    // Only allow if current user created the order
    if (!canActOn(rowData)) {
      return;
    }
    setSelectedRowData(rowData);
    setOrderEntryOpen(true);
  };

  return (
    <Box
      sx={{
        px: "16px",
        py: "0px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Loader overlay when editing ladder group */}
      {editLoading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.4)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={64} sx={{ color: "white" }} />
        </Box>
      )}
      <TableContainer
        component={Paper}
        ref={tableContainerRef}
        sx={{
          height: "100%",
          maxHeight: "100%",
          borderRadius: "0px",
          overflow: "auto",
          position: "relative",
          backgroundColor: MAIN_BG_COLOR,
          "& .MuiTableCell-root": {
            borderBottom: `1px solid ${tokens.colors.defaultGridBorderColor}`,
          },
          "& .vertical-sticky-header": {
            position: "sticky",
            top: 0,
            backgroundColor: HEADER_BG_COLOR,
            zIndex: 4,
          },
          "& .sticky-column": {
            position: "sticky !important",
            left: 0,
            zIndex: 3,
            backgroundColor: source === "inventory" ? MAIN_BG_COLOR : "transparent",
          },
          "& .sticky-header-and-column": {
            position: "sticky !important",
            top: 0,
            left: 0,
            zIndex: 5,
            backgroundColor: HEADER_BG_COLOR,
          },
        }}
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          <Table stickyHeader sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <Droppable droppableId="columns" direction="horizontal" type="column">
                {(provided) => (
                  <TableRow ref={provided.innerRef} {...provided.droppableProps}>
                    {columns.map((column, index) => (
                      <Draggable
                        key={column.key}
                        draggableId={column.key}
                        index={index}
                        isDragDisabled={index === 0 || column.nonDraggable}
                      >
                        {(provided) => (
                          <TableCell
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{
                              backgroundColor: HEADER_BG_COLOR,
                              color: tokens.colors.headerFontColour,
                              padding: cellPadding,
                              fontWeight: 700,
                              height: rowHeight,
                              userSelect: "none",
                              borderBottom: `2px solid ${tokens.colors.defaultGridBorderColor}`,
                              width: column.key === "protocol" ? 100 
                                : column.key === "bondLadderRungId" ? 80 
                                : columnWidths[column.key] || 150,
                              textAlign: column.key === "viewInventory" ? "center" : undefined,
                              cursor: (index === 0 || column.nonDraggable) ? 'default' : 'grab',
                            }}
                            className={
                              source === "inventory" && index === 0
                                ? "vertical-sticky-header sticky-header-and-column"
                                : "vertical-sticky-header"
                            }
                          >
                            {/* Drag handle now on full content wrapper (excluding resize bar) */}
                            <div
                              {...((index === 0 || column.nonDraggable) ? {} : provided.dragHandleProps)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: column.key === "viewInventory"
                                  ? "center"
                                  : column.key === "bondLadderRungId"
                                  ? "center"
                                  : isNumericOrDate(null, column.key)
                                  ? "flex-end"
                                  : isBuySellColumn(column.key)
                                  ? "center"
                                  : "flex-start",
                                height: "100%",
                                width: "100%",
                                paddingRight: "5px",
                                boxSizing: "border-box",
                                cursor: (index === 0 || column.nonDraggable) ? "default" : "grab",
                              }}
                            >
                              <span
                                style={{
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  marginRight: "4px",
                                }}
                                title={column.name}
                              >
                                {column.name}
                              </span>
                              {column.key !== "viewInventory" && column.key !== "bondLadderRungId" && (
                                <span
                                  onClick={() => handleSort(column.key)}
                                  style={{
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {sortConfig.key === column.key ? (
                                    sortConfig.direction === "asc" ? (
                                      <ArrowDropUp fontSize="small" />
                                    ) : (
                                      <ArrowDropDown fontSize="small" />
                                    )
                                  ) : (
                                    <UnfoldMore
                                      fontSize="small"
                                      sx={{ opacity: 0.5, "&:hover": { opacity: 1 } }}
                                    />
                                  )}
                                </span>
                              )}
                            </div>
                            {/* Resize handle */}
                            {column.key !== "bondLadderRungId" && (
                              <div
                                style={{
                                  position: "absolute",
                                  right: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: "5px",
                                  backgroundColor: tokens.colors.bodyBorderColor50,
                                  cursor: (column.key === "protocol" || column.nonResizable) ? "default" : "col-resize",
                                  pointerEvents: (column.key === "protocol" || column.nonResizable) ? "none" : "auto",
                                }}
                                onMouseDown={(e) => startResizing(e, column.key)}
                              />
                            )}
                            {column.key === "bondLadderRungId" && (
                              <div
                                style={{
                                  position: "absolute",
                                  right: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: "5px",
                                  backgroundColor: tokens.colors.bodyBorderColor50,
                                  cursor: "default",
                                  pointerEvents: "none",
                                }}
                              />
                            )}
                          </TableCell>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableRow>
                )}
              </Droppable>
            </TableHead>
            <TableBody>
              {groupedData.map((rowData, rowIndex) => (
                <React.Fragment key={rowData.isLadderGroup ? `ladder-${rowData.bondLadderId}` : `order-${rowData.orderId || rowIndex}`}>
                  {/* Ladder group row */}
                  {rowData.isLadderGroup ? (
                    <TableRow
                      onContextMenu={(e) => handleContextMenu(e, rowData)}
                      sx={{
                        cursor: "pointer",
                        height: rowHeight,
                        backgroundColor: MAIN_BG_COLOR_DARK,
                        borderTop: `2px solid rgba(59, 130, 246, 0.3)`, // Add a subtle border for more distinction
                      }}
                    >
                      <TableCell
                        colSpan={columns.length}
                        onClick={(e) => handleGroupExpand(e, rowData.bondLadderId)}
                        sx={{
                          height: rowHeight,
                          padding: cellPadding,
                          backgroundColor: MAIN_BG_COLOR_DARK,
                          color: tokens.colors.fontColour,
                          cursor: "pointer",
                          borderBottom: `1px solid ${tokens.colors.defaultGridBorderColor}`,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                          <PlayArrow
                            sx={{
                              fontSize: "16px",
                              color: tokens.palette.primary,
                              transform: expandedGroups[rowData.bondLadderId] ? "rotate(90deg)" : "rotate(0deg)",
                              transition: "transform 0.2s",
                              marginRight: "12px",
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: tokens.colors.fontColour,
                              fontWeight: "bold",
                              fontSize: "14px",
                              marginRight: "8px",
                            }}
                          >
                            {rowData.ladderName}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: tokens.colors.fontColour80,
                              fontWeight: 400,
                              fontSize: "14px",
                              marginRight: "8px",
                            }}
                          >
                            (Total {rowData.orders.length.toLocaleString()})
                          </Typography>
                          {/* Handler for Edit icon click on ladder group.
                              Hidden when both ids are present: the group came from a
                              characteristic that is associated with a ladder, so the
                              ladder is not editable from here. */}
                          {canActOn(rowData) &&
                            !isLadderLinkedCharacteristicGroup(rowData) && (
                            <BorderColor
                              sx={{
                                fontWeight: "bold",
                                color: tokens.colors.fontColour,
                                fontSize: "18px",
                                cursor: "pointer",
                                ml: 2,
                              }}
                              onClick={e => {
                                e.stopPropagation();
                                handleEditLadderGroup(rowData);
                              }}
                            />
                          )}
                          {/* Remove Edit button */}
                          {/* Ladder characteristics chips */}
                          {(() => {
                            const chips = getLadderChips(rowData.ladderCharacteristics);
                            if (chips.length === 0) return null;
                            
                            // Create input object for FilterChips - separate metadata and characteristics
                            const chipInput = {};
                            chips.forEach(({ key, value, isMetadata, label }) => {
                              if (isMetadata) {
                                // For metadata, use the label as key for better display
                                const displayKey = label || key;
                                chipInput[displayKey] = value;
                              } else {
                                chipInput[key] = value;
                              }
                            });
                            
                            return (
                              // Prevent click bubbling from chips area to parent TableCell
                              <Box sx={{ display: "flex", alignItems: "center", ml: 2 }} onClick={e => e.stopPropagation()}>
                                <FilterChips
                                  input={chipInput}
                                  readOnly={true}
                                  compact={true}
                                  maxVisibleChips={3}
                                  ladderName={rowData.ladderName} // <-- pass ladderName for modal heading
                                />
                              </Box>
                            );
                          })()}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    /* Regular individual order row */
                    <TableRow
                      onContextMenu={(e) => handleContextMenu(e, rowData)}
                      sx={{
                        cursor: "pointer",
                        height: rowHeight,
                        backgroundColor: MAIN_BG_COLOR,
                        "&:hover": { backgroundColor: HOVER_BG_COLOR },
                      }}
                    >
                      {columns.map((column, colIndex) => {
                        const isNumOrDate = isNumericOrDate(rowData[column.key], column.key);
                        const isBuy = rowData[column.key] === "BUY" || rowData[column.key] === "Buy";
                        const isSell = rowData[column.key] === "SELL" || rowData[column.key] === "Sell";
                        const formattedValue = formatNumberLocal(rowData[column.key], column.key, rowData);
                        // NEW: compute textColor once
                        const textColor = isSell
                          ? tokens.colors.sellFontColor
                          : isBuy
                          ? tokens.colors.buyFontColor
                          : isGreenColumn(column.key)
                          ? tokens.colors.buyFontColor
                          : tokens.colors.fontColour;

                        return (
                          <TableCell
                            key={column.key}
                            sx={{
                              height: rowHeight,
                              padding: cellPadding,
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              textAlign: isBuySellColumn(column.key)
                                ? "center"
                                : column.key === "bondLadderRungId"
                                ? "center"
                                : isTypeLikeColumn(column.key)
                                ? "left"
                                : isNumOrDate
                                ? "right"
                                : "left",
                              backgroundColor: MAIN_BG_COLOR,
                              color: textColor, // ensure cell inherits buy/sell color
                              fontWeight: isSell || isBuy 
                                ? 700 
                                : isBoldColumn(column.key)
                                ? 700
                                : 400,
                              width: column.key === "protocol" ? 100 
                                : column.key === "bondLadderRungId" ? 80 
                                : columnWidths[column.key] || 150,
                            }}
                            className={source === "inventory" && colIndex === 0 ? "sticky-column" : ""}
                            onClick={(e) => {
                              // For staging blotter (order_0-0), only the user who placed the order should open SendToMarket
                              if (isStagingSection) {
                                if (canActOn(rowData)) {
                                  triggerSendToMarket(rowData, e);
                                }
                                return;
                              }

                              // Default behavior for other roles/sections
                              if (column.key === "cusip") {
                                e.stopPropagation();
                                handleCusipClick(rowIndex, rowData);
                              } else if (isBuy || isSell) {
                                handleBuyClick(rowIndex, rowData);
                              } else {
                                handleRowClick(rowIndex, rowData);
                              }
                            }}
                          >
                            {column.render ? (
                              // NEW: force color for rendered cells too
                              <Box sx={{ position: "relative", zIndex: 2, color: textColor }}>
                                {column.render(rowData[column.key], rowData, handleViewJson)}
                              </Box>
                            ) : (
                              <>
                                {(isBuy || isSell) && source === "inventory" && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: isSell
                                        ? withAlpha(tokens.colors.sellBgColor, 0.1)
                                        : withAlpha(tokens.colors.buyBgColor, 0.1),
                                      zIndex: 1,
                                      isolation: "isolate",
                                      mixBlendMode: "normal",
                                    }}
                                  />
                                )}
                                <span
                                  style={{
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    textAlign: isBuySellColumn(column.key)
                                      ? "center"
                                      : column.key === "bondLadderRungId"
                                      ? "center"
                                      : isTypeLikeColumn(column.key)
                                      ? "left"
                                      : isNumOrDate
                                      ? "right"
                                      : "left",
                                    lineHeight: rowHeight,
                                    textDecoration: ((isBuy || isSell) && source === "inventory") || column.key === "cusip"
                                      ? "underline"
                                      : "none",
                                    position: "relative",
                                    zIndex: 2,
                                    color: textColor, // NEW: enforce value color on text
                                    fontWeight: (isSell || isBuy || isBoldColumn(column.key)) ? 700 : 400, // keep bolding rules
                                  }}
                                  title={formattedValue}
                                  onClick={(e) => {
                                    // Prevent opening informative order behind SendToMarket for Traders and PMs on staging
                                    if (isStagingSection) {
                                      e.stopPropagation();
                                      triggerSendToMarket(rowData, e);
                                      return;
                                    }
                                    // Default behavior for other roles/sections
                                    if (column.key === "cusip") {
                                      e.stopPropagation();
                                      handleCusipClick(rowIndex, rowData);
                                    } else if (isBuy || isSell) {
                                      handleBuyClick(rowIndex, rowData);
                                    } else {
                                      handleRowClick(rowIndex, rowData);
                                    }
                                  }}
                                >
                                   {column.key === "bondLadderRungId" && formattedValue && formattedValue !== "---"
                                    ? `Rung ${formattedValue}`
                                    : formattedValue}
                                </span>
                              </>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  )}

                  {/* Expanded rows for ladder group orders */}
                  {rowData.isLadderGroup && (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        sx={{
                          padding: 0,
                          borderBottom: "none",
                          backgroundColor: MAIN_BG_COLOR_DARK, // Use dark background for expanded container
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            overflow: "hidden",
                            transition: "max-height 0.3s ease-in-out, opacity 0.3s ease-in-out",
                            maxHeight: expandedGroups[rowData.bondLadderId]
                              ? (expandedAnimation[rowData.bondLadderId]?.maxHeight ?? 1000)
                              : (expandedAnimation[rowData.bondLadderId]?.maxHeight ?? 0),
                            opacity: expandedGroups[rowData.bondLadderId]
                              ? (expandedAnimation[rowData.bondLadderId]?.opacity ?? 1)
                              : (expandedAnimation[rowData.bondLadderId]?.opacity ?? 0),
                            backgroundColor: LADDER_CHILD_BG_COLOR, // Use the lighter background for container
                            pointerEvents: expandedGroups[rowData.bondLadderId] ? "auto" : "none",
                          }}
                        >
                          {expandedGroups[rowData.bondLadderId] && (
                            <Table sx={{ 
                              tableLayout: "fixed",
                              backgroundColor: LADDER_CHILD_BG_COLOR, // Apply to table as well
                              '& .MuiTableCell-root': {
                                backgroundColor: `${LADDER_CHILD_BG_COLOR} !important`,
                              }
                            }}>
                              <TableBody>
                                {rowData.orders.map((order, orderIndex) => (
                                  <TableRow
                                    key={`order-${order.orderId || orderIndex}`}
                                    sx={{
                                      cursor: "pointer",
                                      height: rowHeight,
                                      backgroundColor: LADDER_CHILD_BG_COLOR,
                                      "&:hover": { backgroundColor: HOVER_BG_COLOR },
                                      // Force background on all child elements
                                      '& .MuiTableCell-root': {
                                        backgroundColor: `${LADDER_CHILD_BG_COLOR} !important`,
                                      }
                                    }}
                                    onContextMenu={(e) => handleContextMenu(e, order)}
                                  >
                                    {columns.map((column, colIndex) => {
                                      const isNumOrDate = isNumericOrDate(order[column.key], column.key);
                                      const isBuy = order[column.key] === "BUY" || order[column.key] === "Buy";
                                      const isSell = order[column.key] === "SELL" || order[column.key] === "Sell";
                                      const formattedValue = formatNumberLocal(order[column.key], column.key, order);
                                      // NEW: compute color for expanded rows too
                                      const textColor = isSell
                                        ? tokens.colors.sellFontColor
                                        : isBuy
                                        ? tokens.colors.buyFontColor
                                        : isGreenColumn(column.key)
                                        ? tokens.colors.buyFontColor
                                        : tokens.colors.fontColour;

                                      return (
                                        <TableCell
                                          key={column.key}
                                          sx={{
                                            height: rowHeight,
                                            padding: cellPadding,
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                            textOverflow: "ellipsis",
                                            textAlign: isBuySellColumn(column.key)
                                              ? "center"
                                              : column.key === "bondLadderRungId"
                                              ? "center"
                                              : isTypeLikeColumn(column.key)
                                              ? "left"
                                              : isNumOrDate
                                              ? "right"
                                              : "left",
                                            backgroundColor: LADDER_CHILD_BG_COLOR,
                                            color: textColor,
                                            fontWeight: isSell || isBuy 
                                              ? 700 
                                              : isBoldColumn(column.key)
                                              ? 700
                                              : 400,
                                            width: column.key === "protocol" ? 100 
                                              : column.key === "bondLadderRungId" ? 80 
                                              : columnWidths[column.key] || 150,
                                            borderBottom: `1px solid ${tokens.colors.defaultGridBorderColor}`,
                                            // Force background with high specificity
                                            '&.MuiTableCell-root': {
                                              backgroundColor: LADDER_CHILD_BG_COLOR,
                                            }
                                          }}
                                        >
                                          {column.render ? (
                                            // NEW: ensure rendered content also gets color
                                            <Box sx={{ position: "relative", zIndex: 2, color: textColor }}>
                                              {column.render(order[column.key], order, handleViewJson)}
                                            </Box>
                                          ) : (
                                            <span
                                              style={{
                                                display: "block",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                textAlign: isBuySellColumn(column.key)
                                                  ? "center"
                                                  : column.key === "bondLadderRungId"
                                                  ? "center"
                                                  : isTypeLikeColumn(column.key)
                                                  ? "left"
                                                  : isNumOrDate
                                                  ? "right"
                                                  : "left",
                                                lineHeight: rowHeight,
                                                textDecoration: ((isBuy || isSell) && source === "inventory") || column.key === "cusip"
                                                  ? "underline"
                                                  : "none",
                                                color: textColor, // NEW
                                                fontWeight: (isSell || isBuy || isBoldColumn(column.key)) ? 700 : 400, // keep bolding rules
                                              }}
                                              title={formattedValue}
                                              onClick={(e) => {
                                                // Prevent opening informative order behind SendToMarket for Traders and PMs on staging
                                                if (isStagingSection) {
                                                  e.stopPropagation();
                                                  triggerSendToMarket(order, e);
                                                  return;
                                                }
                                                // Default behavior for other roles/sections
                                                if (column.key === "cusip") {
                                                  e.stopPropagation();
                                                  handleCusipClick(orderIndex, order);
                                                } else if (isBuy || isSell) {
                                                  handleBuyClick(orderIndex, order);
                                                } else {
                                                  handleRowClick(orderIndex, order);
                                                }
                                              }}
                                            >
                                            {column.key === "bondLadderRungId" && formattedValue && formattedValue !== "---"
                                            ? `Rung ${formattedValue}`
                                            : formattedValue}
                                            </span>
                                          )}
                                        </TableCell>
                                      );
                                    })}
                                  </TableRow>
                                ))} 
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))} 
            </TableBody>
          </Table>
        </DragDropContext>
      </TableContainer>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {selectedRowData && canActOn(selectedRowData) ? (
          <>
            <MenuItem onClick={() => { handleCloseContextMenu(); handleSendToMarket(); }}>
              {isLadderLevelSendToMarket
                ? "Send to market (Ladder Level)"
                : "Send to market"}
            </MenuItem>
            {!selectedRowData.isLadderGroup && (
              <MenuItem onClick={() => { handleCloseContextMenu(); handleManualTradeCapture(); }}>
                Manual Trade Capture
              </MenuItem>
            )}
          </>
        ) : (
          <MenuItem disabled>Only the user who placed this order can send it to the market.</MenuItem>
        )}
      </Menu>

      {orderEntryOpen && selectedRowData && (
        <OrderEntry
          open={orderEntryOpen}
          onClose={() => setOrderEntryOpen(false)}
          source={orderEntrySource}
          secondarySource={orderEntrySecondarySource}
          initialData={selectedRowData}
          readOnly={false}
          token={localStorage.getItem("token")}
          onOrderAdded={onOrderAdded}
        />
      )}

      {manualTradeOpen && (
        <TradeEntryPopup
          open={manualTradeOpen}
            onClose={() => setManualTradeOpen(false)}
          onTradeAdded={onOrderAdded}
          prefillData={manualTradePrefill}
          source="staging-blotter"
        />
      )}
    </Box>
  );
};

export default StagingBlotterTable;
